import React, { useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Mail, ArrowLeft } from 'lucide-react';

const ClientLogin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email) {
      toast.error('Email is required');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/clients/login`, { email: formData.email });
      localStorage.setItem('client_token', response.data.token);
      localStorage.setItem('client_name', response.data.name);
      toast.success(`Welcome back, ${response.data.name}!`);
      setTimeout(() => navigate('/'), 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed. Please check your email.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center p-4 relative">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/3 -right-32 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-md relative z-10">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-[#94A3B8] hover:text-[#D4AF37] mb-8 transition-colors duration-300"
          data-testid="back-to-home-link"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back to Home</span>
        </button>

        <div className="bg-[#131A38] border border-white/8 rounded-2xl p-8 md:p-10">
          <div className="mb-8">
            <p className="overline text-[#D4AF37] mb-3">Welcome Back</p>
            <h1 className="font-heading text-3xl md:text-4xl font-semibold text-white tracking-tight leading-tight">
              Client Sign In
            </h1>
            <p className="text-[#94A3B8] mt-3 text-sm leading-relaxed">
              Sign in with your registered email to access your account.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-[#94A3B8] text-xs uppercase tracking-wider font-medium mb-2 block">
                Email Address
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3.5 w-4 h-4 text-[#475569]" />
                <Input
                  id="email"
                  type="email"
                  data-testid="login-email-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="you@example.com"
                  required
                  className="pl-10 bg-[#0A0E27] border-white/10 text-white placeholder:text-[#475569] focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 h-12 rounded-xl"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#D4AF37] hover:bg-[#E2C364] text-[#0A0E27] font-semibold h-12 rounded-full text-sm tracking-wide transition-colors duration-300"
              data-testid="login-submit-button"
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </Button>

            <p className="text-center text-sm text-[#94A3B8]">
              Don't have an account?{' '}
              <button
                type="button"
                onClick={() => navigate('/register')}
                className="text-[#D4AF37] hover:text-[#E2C364] transition-colors duration-300"
                data-testid="register-link"
              >
                Register here
              </button>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ClientLogin;
